import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Buildings, CaretDown, Check, Eye } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * F2: Org switcher dropdown for super_admins. Mounted in the header.
 * - Hidden entirely for non-super-admin roles.
 * - "Union view" = no X-Active-Org header; user sees aggregate across all orgs.
 * - Selecting an org sets the X-Active-Org header on every axios call and
 *   re-fetches /auth/me so `acting_cross_org` is correct.
 */
export default function OrgSwitcher() {
  const { user, activeOrgId, setActiveOrgId } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!user || !["super_admin", "admin"].includes(user.role)) return;
    axios.get(`${API}/admin/organizations`).then(r => setOrgs(r.data || [])).catch(() => {});
  }, [user]);

  // Re-fetch the org list whenever the dropdown opens, OR when another part
  // of the app dispatches `holotrak:orgs-updated` (e.g. Admin Panel after
  // creating a new tenant). Without this, newly-created orgs only appeared
  // after a hard page reload.
  useEffect(() => {
    if (!open) return;
    if (!user || !["super_admin", "admin"].includes(user.role)) return;
    axios.get(`${API}/admin/organizations`).then(r => setOrgs(r.data || [])).catch(() => {});
  }, [open, user]);

  useEffect(() => {
    const refetch = () => {
      if (!user || !["super_admin", "admin"].includes(user.role)) return;
      axios.get(`${API}/admin/organizations`).then(r => setOrgs(r.data || [])).catch(() => {});
    };
    window.addEventListener("holotrak:orgs-updated", refetch);
    return () => window.removeEventListener("holotrak:orgs-updated", refetch);
  }, [user]);

  if (!user || !["super_admin", "admin"].includes(user.role)) return null;

  const activeOrg = orgs.find(o => o.id === activeOrgId);
  const label = activeOrg ? activeOrg.name : "All Organizations";

  return (
    <div ref={ref} className="relative" data-testid="org-switcher">
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(!open)}
        className={`h-8 text-[11px] gap-1 ${
          activeOrgId
            ? "border-amber-700/60 bg-amber-500/10 text-amber-200 hover:bg-amber-500/15"
            : "border-zinc-700 text-zinc-300 hover:bg-zinc-800"
        }`}
        data-testid="org-switcher-trigger"
      >
        <Buildings size={11} weight="duotone" />
        <span className="hidden lg:inline max-w-[140px] truncate">{label}</span>
        <CaretDown size={9} className="ml-1 opacity-60" />
      </Button>

      {open && (
        <div className="absolute right-0 mt-1 w-72 bg-zinc-950 border border-zinc-800 rounded shadow-xl z-50"
          data-testid="org-switcher-menu">
          <div className="px-3 py-2 border-b border-zinc-800">
            <div className="text-[9px] uppercase tracking-wider text-zinc-500">Acting as</div>
          </div>
          <button
            className={`w-full text-left px-3 py-2 hover:bg-zinc-900 flex items-center justify-between text-[12px] ${
              !activeOrgId ? "bg-zinc-900" : ""
            }`}
            onClick={() => { setActiveOrgId(null); setOpen(false); }}
            data-testid="org-switcher-all"
          >
            <div>
              <div className="text-zinc-200">All Organizations</div>
              <div className="text-[10px] text-zinc-500">Platform-wide aggregate view</div>
            </div>
            {!activeOrgId && <Check size={12} className="text-violet-400" />}
          </button>
          <div className="border-t border-zinc-800 my-1" />
          <div className="max-h-72 overflow-y-auto">
            {orgs.map((o) => (
              <button
                key={o.id}
                className={`w-full text-left px-3 py-2 hover:bg-zinc-900 flex items-center justify-between text-[12px] ${
                  o.id === activeOrgId ? "bg-zinc-900" : ""
                } ${o.status && o.status !== "active" ? "opacity-50" : ""}`}
                onClick={() => { setActiveOrgId(o.id); setOpen(false); }}
                data-testid={`org-switcher-pick-${o.slug || o.id}`}
              >
                <div className="min-w-0">
                  <div className="text-zinc-200 truncate flex items-center gap-1.5">
                    {o.name}
                    {o.status && o.status !== "active" && (
                      <span className="text-[9px] uppercase tracking-wider px-1 rounded bg-red-500/15 text-red-300 border border-red-700/40">{o.status}</span>
                    )}
                  </div>
                  <div className="text-[10px] text-zinc-500 truncate">{o.plan || "—"} · <span className="font-mono">{o.id.slice(0,8)}…</span></div>
                </div>
                {o.id === activeOrgId && <Check size={12} className="text-violet-400" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * F2 (banner half): When a super_admin is acting cross-org, a top banner
 * is rendered to prevent confusion. Auto-hidden during union view + home view.
 */
export function ActingAsBanner() {
  const { user, activeOrgId, setActiveOrgId } = useAuth();
  const [orgs, setOrgs] = useState([]);
  useEffect(() => {
    if (!user || !["super_admin", "admin"].includes(user.role) || !activeOrgId) return;
    axios.get(`${API}/admin/organizations`).then(r => setOrgs(r.data || [])).catch(() => {});
  }, [user, activeOrgId]);
  if (!user || !["super_admin","admin"].includes(user.role) || !activeOrgId) return null;
  const active = orgs.find(o => o.id === activeOrgId);
  return (
    <div className="bg-amber-500/10 border-b border-amber-700/40 text-amber-200 px-4 py-1.5 flex items-center justify-between text-[11px]"
      data-testid="acting-as-banner">
      <div className="flex items-center gap-2">
        <Eye size={12} weight="duotone" />
        <span>
          <span className="uppercase tracking-wider font-bold text-amber-300">Acting as: {active ? active.name : "—"}</span>
          <span className="text-amber-200/70 ml-2">— actions in this session affect this tenant only.</span>
        </span>
      </div>
      <button onClick={() => setActiveOrgId(null)}
        className="text-amber-200 hover:text-amber-100 underline underline-offset-2 text-[11px]"
        data-testid="acting-as-exit-btn">
        Return to platform view
      </button>
    </div>
  );
}
